// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.366Z
// Collection: webhookDeliveries/$deliveryId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Outbound webhook delivery tracking with retry state. Service-only writes; tenant owner reads for observability.
 */

export interface WebhookDeliveriesRequest {
  sessionId: string;
  appid: string;
  url: string;
  payloadHash: string;
  status: string;
  attemptCount: number | TimeOperation | IncrementOperation | TokenAmount;
  lastError?: string;
  nextAttemptAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  lastSentAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WebhookDeliveriesResponse {
  sessionId: string;
  appid: string;
  url: string;
  payloadHash: string;
  status: string;
  attemptCount: number;
  lastError?: string;
  nextAttemptAt?: number;
  lastSentAt?: number;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a WebhookDeliveries operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWebhookDeliveries(deliveryId: string, data: WebhookDeliveriesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `webhookDeliveries/${deliveryId}`, document: doc };
}

/**
 * Service-only. Backend creates a delivery row when scheduling an outbound webhook with status='pending' and attemptCount=0. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWebhookDeliveries(deliveryId: string, data: WebhookDeliveriesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`webhookDeliveries/${deliveryId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting WebhookDeliveries: ${formatError(error)}`);
    return false;
  }
}

export type WebhookDeliveriesRequestUpdate = Partial<WebhookDeliveriesRequest>;

/**
 * Build a WebhookDeliveries update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateWebhookDeliveries(deliveryId: string, data: WebhookDeliveriesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `webhookDeliveries/${deliveryId}`, document: doc };
}

/**
 * Service-only. Backend updates status (pending→sent/failed), increments attemptCount, sets lastError, nextAttemptAt, lastSentAt across retry attempts. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateWebhookDeliveries(deliveryId: string, data: WebhookDeliveriesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`webhookDeliveries/${deliveryId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating WebhookDeliveries: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
   (Get Single Item)
 */
export async function getWebhookDeliveries(deliveryId: string): Promise<WebhookDeliveriesResponse | null> {
  try {
    const result = await get(`webhookDeliveries/${deliveryId}`);
    return result as WebhookDeliveriesResponse | null;
  } catch (error) {
    console.error(`Error getting WebhookDeliveries: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single WebhookDeliveries document. (
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
  )
 */
export function subscribeWebhookDeliveries(callback: (data: WebhookDeliveriesResponse | null) => void, deliveryId: string): Promise<() => Promise<void>> {
  return subscribe(`webhookDeliveries/${deliveryId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WebhookDeliveriesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to WebhookDeliveries at path '${`webhookDeliveries/${deliveryId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many WebhookDeliveries items from collection webhookDeliveries
 
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
  
 */
export async function getManyWebhookDeliveries(filter: string = ""): Promise<WebhookDeliveriesResponse[]> {
  try {
    const results = await get("webhookDeliveries", { prompt: filter });
    return Array.isArray(results) ? results as WebhookDeliveriesResponse[] : [];
  } catch (error) {
    console.error(`Error getting WebhookDeliveries collection at path 'webhookDeliveries': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WebhookDeliveries collection at webhookDeliveries
 
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
  
 */
export function subscribeManyWebhookDeliveries(callback: (data: WebhookDeliveriesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("webhookDeliveries", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WebhookDeliveriesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WebhookDeliveries collection at path '${"webhookDeliveries"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all WebhookDeliveries items from collection webhookDeliveries
 
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
  
 */
export async function getAllWebhookDeliveries(filter: string = ""): Promise<WebhookDeliveriesResponse[]> {
  try {
    const results = await get("webhookDeliveries", { prompt: filter });
    return Array.isArray(results) ? results as WebhookDeliveriesResponse[] : [];
  } catch (error) {
    console.error(`Error getting WebhookDeliveries collection at path 'webhookDeliveries': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WebhookDeliveries collection at webhookDeliveries
 
  Read Operation Details: Service, admin, or tenant owner (resolved via tenants/$appid.owner) can read delivery state for observability.
  
 */
export function subscribeAllWebhookDeliveries(callback: (data: WebhookDeliveriesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("webhookDeliveries", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WebhookDeliveriesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WebhookDeliveries collection at path '${"webhookDeliveries"}': ${formatError(error)}`);
    }
  });
}

